import { useEffect, useState } from 'react';
import { useT } from '~/i18n';
import { getPendingReferral } from '~/lib/referralCapture';
import { fetchReferralPartner } from '~/lib/referralPartner';

/**
 * El aviso de consentimiento de quien llega con el código de un socio:
 * «Te registraste con el código de {socio}. Compartimos tu correo y el estado
 * de tu plan con quien te invitó, para que pueda acompañarte.»
 *
 * POR QUÉ: el socio ve en su panel el correo completo y el estado del plan de
 * cada referido, para hacerle seguimiento. La Ley 1581 pide que la persona sepa
 * qué se comparte y con quién ANTES de que pase, no en una página aparte.
 *
 * `who` es el nombre del socio; sin nombre, el código.
 */
export function ReferralConsentNotice({ who, className = '' }: { who: string; className?: string }) {
  const t = useT('common');
  return (
    <p className={`text-sm ${className}`.trim()} role="note" data-testid="referral-consent">
      {t.referralConsent.notice(who)}
    </p>
  );
}

/**
 * El mismo aviso en el shell del candidato, mientras hay un `?ref=` pendiente.
 *
 * Va en el shell (y no sólo en la oferta del plan) porque ahí se ve desde la
 * PRIMERA pantalla del alta, y la atribución se estampa sola al terminarla
 * (`syncPendingReferral`): la oferta llega después, cuando el socio ya puede
 * ver a la persona. El código se lee UNA vez al montar y se queda en estado:
 * el sync lo borra del navegador apenas se estampa, y el aviso no puede
 * desaparecer en el mismo instante en que se vuelve verdad.
 *
 * Sólo si el código es de un socio: un cupón de campaña no le muestra nada a
 * nadie y no hay qué avisar.
 */
export function PendingReferralNotice() {
  const [code] = useState(() => getPendingReferral());
  const [who, setWho] = useState<string | null>(null);

  useEffect(() => {
    if (!code) return;
    let alive = true;
    void fetchReferralPartner(code).then((result) => {
      if (!alive) return;
      if (result.status === 'partner') setWho(result.name ?? code);
      else if (result.status === 'unknown') setWho(code);
    });
    return () => { alive = false; };
  }, [code]);

  if (!who) return null;
  return (
    <div className="alert alert-info mb-4" role="status">
      <ReferralConsentNotice who={who} />
    </div>
  );
}
